import { Suspense, useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import {
  CatmullRomCurve3,
  Color,
  CurvePath,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PointLight,
  QuadraticBezierCurve3,
  Quaternion,
  SphereGeometry,
  TubeGeometry,
  Vector3,
} from "three";
import * as THREE from "three";
import CustomShaderMaterial from "three-custom-shader-material/vanilla";
import { useObjectCallback, useSheet } from "../../../../hooks/useTheatre";
import { findObjectStartsWithKey } from "../../../../utils/FindObjects";

import { useAnimations, Tube } from "@react-three/drei";

import WaterStream, { GridWaterStreams, WaterStreams } from "./Water";
import { degToRad } from "three/src/math/MathUtils";
import { useFrame, useThree } from "@react-three/fiber";
import RainEffectInsances from "./SteamParticelsBAS";
import SteamParticlesInstances from "./SteamParticelsBAS";
// import SteamParticles, { RainEffectVfx } from "./SteamParticles";

const LightName = "Teller_Light_asdfadsfadsf";

const RotaryTableAnimation = ({ object3d, action, ...props }) => {
  const sheet = useSheet("EFD", "HARDLINE_ROTARYTABLE", "0");

  const waterRef_R = useRef();
  const waterRef_L = useRef();

  const [steam1active, setsteam1active] = useState(false);
  const steam1activeRef = useRef(false);

  const [steam2active, setsteam2active] = useState(false);
  const steam2activeRef = useRef(false);

  const tube1 = useRef();
  const tube2 = useRef();

  const { scene } = useThree();

  const animationConfig = useMemo(() => {
    return {
      animationTime: 0,
      lightIntesity: 0,
      heatIntensity: 0,
      uHeatFallof: 0,
      water: 0,
      uHardened: 0,
      steam1: false,
      steam2: false,
      steam2Mounted: false,
    };
  }, []);

  useEffect(() => {
    action.reset().play().fadeIn(0.5);
    action.play();
    action.paused = true;
    sheet.sequence.position = 0;
    sheet.sequence.play({ iterationCount: Infinity });

    return () => {
      sheet.sequence.position = 0;
      sheet.sequence.pause();
      action.time = 0;
      action.fadeOut(0.5).stop().reset();
    };
  }, []);

  //disable Door for this animation
  useEffect(() => {
    var ture = findObjectStartsWithKey("Anim_M1_HL_Tuerfront", object3d);
    ture.visible = false;

    return () => {
      var ture = findObjectStartsWithKey("Anim_M1_HL_Tuerfront", object3d);
      ture.visible = true;
    };
  }, []);

  const teller1 = findObjectStartsWithKey("Hardline_Teller_Werkstueck_01_glow", object3d);

  const Light = useMemo(() => {
    var l = findObjectStartsWithKey(LightName, object3d);
    if (l) return l;

    var target = findObjectStartsWithKey("Teller_LightPosition", object3d);

    var sphereGeo = new SphereGeometry(0.05);
    var sphereMat = new MeshBasicMaterial({
      color: new Color(0x1100ff),
      depthTest: false,
      wireframe: true,
      transparent: true,
    });

    var debugSphere = new Mesh(sphereGeo, sphereMat);

    var light = new PointLight(new Color("#ff0000"), 0);

    // light.add(debugSphere);
    light.position.copy(target.position);
    light.name = LightName;
    light.distance = 0.25;
    light.decay = 2;
    target.add(light);
    light.position.copy(new Vector3());

    return light;
  }, []);

  useEffect(() => {
    if (waterRef_R.current) {
      var target2 = findObjectStartsWithKey("Water_Target_R", object3d);

      target2.add(waterRef_R.current);
      waterRef_R.current.position.copy(new Vector3());
    }
    if (waterRef_L.current) {
      var target2_L = findObjectStartsWithKey("Water_Target_L", object3d);

      target2_L.add(waterRef_L.current);
      waterRef_L.current.position.copy(new Vector3());
    }
  }, [waterRef_R.current]);

  useObjectCallback(
    sheet,
    "Animation",
    animationConfig,
    ({ animationTime, lightIntesity, heatIntensity, uHeatFallof, water, uHardened, steam1, steam2 }) => {
      action.time = sheet.sequence.position;
      if (Light) {
        Light.intensity = lightIntesity;
      }
      if (teller1) {
        if (teller1.material.userData.shader) {
          teller1.material.userData.shader.uniforms.uHeat.value = heatIntensity;
          teller1.material.userData.shader.uniforms.uHardened.value = uHardened;
          teller1.material.userData.shader.uniforms.uHeatFallof.value = uHeatFallof;
        }
      }
      if (waterRef_R.current) {
        waterRef_R.current.scale.x = water;
        waterRef_L.current.scale.x = water;
      }
      if (steam1activeRef.current != steam1) {
        steam1activeRef.current = steam1;
        setsteam1active(steam1);
      }
      if (steam2activeRef.current != steam2) {
        steam2activeRef.current = steam2;
        setsteam2active(steam2);
      }
      if (tube1.current) {
        var currentGeo = tube1.current.geometry;
        tube1.current.geometry = new TubeGeometry(getTube1Curve(), 30, 0.018);
        currentGeo.dispose();
      }
      if (tube2.current) {
        var currentGeo2 = tube2.current.geometry;
        tube2.current.geometry = new TubeGeometry(getTube2Curve(), 30, 0.018);
        currentGeo2.dispose();
      }
    },
  );

  //Tubes
  const tube1Start = useMemo(() => {
    return findObjectStartsWithKey("Tube_1_Start", object3d);
  }, []);

  const tube1End = useMemo(() => {
    return findObjectStartsWithKey("Tube_1_end", object3d);
  }, []);

  const root = useMemo(() => {
    return findObjectStartsWithKey("Hardline_Root", object3d);
  }, []);

  const [startPosition, handle1, handle2, handleEnd1, targetPos] = useMemo(() => {
    var vectors = [];
    for (let index = 0; index < 5; index++) {
      vectors.push(new Vector3());
    }
    return vectors;
  }, []);

  const handle1Direction = useMemo(() => {
    return new Vector3(0.15, -1.0, 0.0).multiplyScalar(0.05);
  });

  const handle2Direction = useMemo(() => {
    return new Vector3(0.25, -0.2, 0.0).multiplyScalar(0.05);
  });

  const handleEnd1Direction = useMemo(() => {
    return new Vector3(0, 0.5, -0.051).multiplyScalar(0.1);
  });

  const getTube1Curve = () => {
    tube1Start.getWorldPosition(startPosition);

    tube1Start.getWorldPosition(handle1);

    // var startQuat = new Quaternion();

    // tube1Start.getWorldQuaternion(startQuat);

    handle1.add(handle1Direction);

    handle2.copy(handle1);
    handle2.add(handle2Direction);

    tube1End.getWorldPosition(handleEnd1);

    handleEnd1.add(handleEnd1Direction);

    tube1End.getWorldPosition(targetPos);

    var Curve1 = new CatmullRomCurve3([
      startPosition,
      handle1,
      // handle2,
      handleEnd1,
      targetPos,
    ]);

    return Curve1;
  };

  const tube2Start = useMemo(() => {
    return findObjectStartsWithKey("Tube_2_start", object3d);
  }, []);

  const tube2End = useMemo(() => {
    return findObjectStartsWithKey("Tube_2_end", object3d);
  }, []);

  const handle1Direction2 = useMemo(() => {
    return new Vector3(-0.0, -1.0, 0.15).multiplyScalar(0.05);
  });

  const handle2Direction2 = useMemo(() => {
    return new Vector3(0.9, 0.0, 0.0).multiplyScalar(0.1);
  });

  const handleEnd1Direction2 = useMemo(() => {
    return new Vector3(-0.1, 0.5, 0.01).multiplyScalar(0.051);
  });

  const getTube2Curve = () => {
    tube2Start.getWorldPosition(startPosition);

    tube2Start.getWorldPosition(handle1);

    handle1.add(handle1Direction2);

    handle2.copy(handle1);
    handle2.add(handle2Direction2);

    tube2End.getWorldPosition(handleEnd1);

    handleEnd1.add(handleEnd1Direction2);

    tube2End.getWorldPosition(targetPos);

    var Curve1 = new CatmullRomCurve3([
      startPosition,
      handle1,
      // handle2,
      handleEnd1,
      targetPos,
    ]);

    return Curve1;
  };

  useEffect(() => {
    var tubeGeo = new TubeGeometry(getTube1Curve(), 10, 0.018);
    var tubeGeo2 = new TubeGeometry(getTube2Curve(), 10, 0.018);

    var tubeMat = new MeshStandardMaterial({
      // color: 0x0d3a8a,
      color: 0x1d242b,
      metalness: 0.1,
      roughness: 0.8,
      side: DoubleSide,
    });
    var tubeMesh = new Mesh(tubeGeo, tubeMat);
    var tubeMesh2 = new Mesh(tubeGeo2, tubeMat);

    scene.add(tubeMesh);
    tube1.current = tubeMesh;

    scene.add(tubeMesh2);
    tube2.current = tubeMesh2;

    return () => {
      // tube1.current.dispose();
      scene.remove(tube1.current);
      // tube2.current.dispose();
      scene.remove(tube2.current);
    };
  }, []);

  return (
    <>
      <SteamParticlesInstances
        position={[-0.0007, 1.0996, -0.2003]}
        active={steam1active}
        // rotationSpeed={-0.005}
      />
      <SteamParticlesInstances position={[0.2011, 1.057, 0]} active={steam2active} rotationSpeed={-0.005} />
      <group ref={waterRef_R} scale={[1, 1, 1]}>
        <GridWaterStreams />
      </group>
      <group ref={waterRef_L} scale={[1, 1, 1]}>
        <GridWaterStreams />
      </group>
    </>
  );
};

export default RotaryTableAnimation;
